import * as React from 'react';
import styled from 'styled-components';
import { LibraryBooks } from '@material-ui/icons';
import useTablePage from '../../hooks/useTablePage';
import DashboardLayout from '../../layouts/DashboardLayout';
import {
  getFormSetupList,
  createFormSetup,
  editFormSetup,
  deleteFormSetup,
} from '../../api/formSetup';
import ActionsPanel from '../../organisms/ActionsPanel';
import Table from '../../organisms/Table';
import { convertTableBoolean } from '../../utils/convertTableValues';
import tableColumnsFormatter from '../../utils/tableColumnsFormatter';
import {
  ModalDefaultDraggable,
  ModalDeleteConfirmation,
} from '../../molecules';
import FormSetupForm from '../../forms/FormSetupForm';
import { getJurisdictions } from '../../api/jurisdiction';
import { GET_ALL_QUERY_PARAMS } from '../../constants';
import EditFormSetupPlaceholders from '../../organisms/EditFormSetupPlaceholders';

const FormSetupPage = () => {
  const {
    search,
    setSearch,
    isModalOpen,
    updateTable,
    modalTitle,
    values,
    errors,
    modalErrorTitle,
    tableManuallyUpdate,
    createOrUpdate,
    deleteById,
    selectedItem,
    setSelectedItem,
    handleAdd,
    handleEdit,
    handleDelete,
    handleCloseModal,
    // handleCloseModalInfo,
  } = useTablePage({
    createEndpoint: createFormSetup,
    updateEndpoint: editFormSetup,
    deleteEndpoint: deleteFormSetup,
    modalCreateTitle: 'Add New Form',
  });

  const [
    editPlaceholdersData,
    setEditPlaceholdersData,
  ] = React.useState(null);

  const [
    selectedFontSize,
    setSelectedFontSize,
  ] = React.useState(12);

  const modifiedGetFormSetupRequest = params =>
    Promise.all([
      getFormSetupList(params),
      getJurisdictions({ ...GET_ALL_QUERY_PARAMS }),
    ]).then(
      ([formSetupResponse, jurisdictionsResponse]) => {
        return {
          ...formSetupResponse,
          data: {
            ...formSetupResponse.data,
            results: formSetupResponse.data.results.map(
              form => {
                const names =
                  jurisdictionsResponse.data.results
                    ?.filter(jur =>
                      form.jurisdictions.includes(jur.id),
                    )
                    .map(el => el.name) ?? [];
                // Long jurisdiction lists blow up the row height — show the
                // first few names plus a "+N more" summary to keep it compact.
                const jurisdiction =
                  names.length > 3
                    ? `${names
                        .slice(0, 3)
                        .join(', ')}  +${names.length - 3} more`
                    : names.join(', ');
                return { ...form, jurisdiction, jurisdictionNames: names };
              },
            ),
          },
        };
      },
    );

  const columns = column => {
    if (
      [
        'reportScope',
        'type',
        'documents',
        'formMarkup',
        'file',
        'jurisdictions',
        'jurisdictionNames',
        'isDeleted',
        'fontSize',
        'isDefault',
        // Columns are derived from whatever keys the API returns, so adding
        // source_file to FormSerializer made a "Source File" column appear on
        // its own. It is plumbing for the text-based PDF renderer, not
        // something to show.
        'sourceFile',
      ].includes(column)
    ) {
      return null;
    }

    switch (column) {
      case 'name':
        return 'Form Name';
      case 'visibleFilename':
        return 'Path';
      default:
        return tableColumnsFormatter(column);
    }
  };

  const columnsOrder = [
    'id',
    'name',
    'description',
    'jurisdiction',
    'file',
  ];

  const [jurisdictionModal, setJurisdictionModal] = React.useState<any>(null);

  const downloadFile = row => {
    if (row.file) {
      window.open(row.file);
    }
  };

  const onCellClick = (row, column?: string) => {
    if (column === 'jurisdiction') {
      setJurisdictionModal(row);
    } else {
      downloadFile(row);
    }
  };

  const additionalActions = row => {
    return [
      {
        title: 'Edit Placeholders',
        handler: () => setEditPlaceholdersData(row),
        Icon: LibraryBooks,
      },
    ];
  };

  return (
    <DashboardLayout>
      <PageWrapper>
        <ActionsPanel
          onSearch={setSearch}
          onAdd={handleAdd}
        />
      <Container>
        <Table
          customSearch
          searchQuery={search}
          columns={columns}
          columnsOrder={columnsOrder}
          rows={modifiedGetFormSetupRequest}
          onEdit={handleEdit}
          onDelete={handleDelete}
          formatValue={convertTableBoolean}
          manuallyUpdate={tableManuallyUpdate}
          cellCallbackFnIsOn={['visibleFilename', 'jurisdiction']}
          handleCellClick={onCellClick}
          additionalActions={additionalActions}
        />
      </Container>
      {selectedItem && (
        <ModalDeleteConfirmation
          onCancel={() => setSelectedItem(null)}
          onConfirm={() => deleteById(selectedItem.id)}
        >
          <strong>{selectedItem.name}</strong>
        </ModalDeleteConfirmation>
      )}
      {jurisdictionModal && (
        <ModalDefaultDraggable
          title={`Jurisdictions — ${jurisdictionModal.name || ''}`}
          onClose={() => setJurisdictionModal(null)}
          narrow
        >
          <JurisdictionList>
            {(jurisdictionModal.jurisdictionNames || []).map(
              (n: string) => (
                <li key={n}>{n}</li>
              ),
            )}
          </JurisdictionList>
        </ModalDefaultDraggable>
      )}
      <EditFormSetupPlaceholders
        data={editPlaceholdersData}
        setData={setEditPlaceholdersData}
        updateTable={updateTable}
        setSelectedFontSize={setSelectedFontSize}
        selectedFontSize={selectedFontSize}
      />
      {isModalOpen && (
        <StyledModalDraggable
          onClose={handleCloseModal}
          title={modalTitle}
        >
          <FormSetupForm
            onSubmit={createOrUpdate}
            initialValues={values}
            initialErrors={errors}
            modalError={modalErrorTitle.length > 0}
            setSelectedFontSize={setSelectedFontSize}
            selectedFontSize={selectedFontSize}
          />
        </StyledModalDraggable>
      )}
      </PageWrapper>
    </DashboardLayout>
  );
};

const PageWrapper = styled.div`
  width: 100%;
  padding: 20px 16px 0;
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  @media (max-width: 840px) {
    padding: 12px 12px 0;
  }
`;

const Container = styled.div`
  margin: 20px 0 0 0;
`;

const JurisdictionList = styled.ul`
  list-style: disc;
  margin: 0;
  padding: 4px 8px 4px 24px;
  columns: 2;
  font-size: 0.875rem;
  color: var(--mainText);

  li {
    margin-bottom: 6px;
    break-inside: avoid;
  }

  @media (max-width: 540px) {
    columns: 1;
  }
`;

const StyledModalDraggable = styled(ModalDefaultDraggable)`
  height: 100%;
  overflow: inherit;
`;

export default FormSetupPage;
